/**
 * Homepage behaviour. There is only one piece of it: the outfit tabs.
 *
 * The section renders every outfit, stacked, and this turns them into tabs.
 * That order matters — a customer whose JavaScript has not arrived sees all
 * the outfits rather than one panel and two dead buttons.
 *
 * Re-runs on Shopify's section:load event so the theme editor shows the tabs
 * immediately after an edit instead of only on a full reload.
 */
(function () {
  'use strict';

  function setup(root) {
    if (root.__tcLookReady) return;
    var tabs = root.querySelectorAll('[data-tc-tab]');
    var panels = root.querySelectorAll('[data-tc-panel]');
    if (tabs.length < 2 || panels.length < 2) return;
    root.__tcLookReady = true;

    function show(which) {
      for (var i = 0; i < panels.length; i++) {
        var on = panels[i].getAttribute('data-tc-panel') === which;
        panels[i].hidden = !on;
      }
      for (var j = 0; j < tabs.length; j++) {
        var active = tabs[j].getAttribute('data-tc-tab') === which;
        tabs[j].classList.toggle('is-on', active);
        tabs[j].setAttribute('aria-selected', active ? 'true' : 'false');
      }
    }

    for (var k = 0; k < tabs.length; k++) {
      tabs[k].addEventListener('click', function (e) {
        show(e.currentTarget.getAttribute('data-tc-tab'));
      });
    }

    /* Only now, once the switching works, is it safe to hide anything. */
    root.setAttribute('data-tc-tabs', 'on');
    show(tabs[0].getAttribute('data-tc-tab'));
  }


  /**
   * The look's total follows the sizes chosen.
   *
   * Prices can differ between variants — a 44L costs more than a 36S on some
   * suits — so a total printed from the default variants would be wrong the
   * moment somebody picks their own size. Recalculated from the selected
   * options; falls back to the price the server printed for any piece not yet
   * chosen.
   */
  function totals(form) {
    if (form.__tcTotals) return;
    form.__tcTotals = true;
    var out = form.querySelector('[data-tc-total]');
    if (!out) return;

    function money(pence) {
      return '\u00A3' + (pence / 100).toFixed(2);
    }

    function recalc() {
      var sum = 0;
      var picks = form.querySelectorAll('.tc-pick');
      for (var i = 0; i < picks.length; i++) {
        var sel = picks[i].querySelector('[data-tc-piece]');
        var priceEl = picks[i].querySelector('[data-tc-piece-price]');
        var pence = priceEl ? parseInt(priceEl.getAttribute('data-tc-piece-price'), 10) : 0;
        if (sel && sel.value) {
          var opt = sel.options[sel.selectedIndex];
          var chosen = opt && opt.getAttribute('data-price');
          if (chosen) pence = parseInt(chosen, 10);
        }
        if (priceEl && !isNaN(pence)) priceEl.textContent = money(pence);
        if (!isNaN(pence)) sum += pence;
      }
      out.textContent = money(sum);
    }

    /* Show the colour they picked. Symmetry's images carry a srcset, and a
       srcset outranks src — setting src alone leaves the old picture on screen,
       which is exactly how this looked broken. So the srcset goes too. */
    function reshoot(pick, sel) {
      var opt = sel.options[sel.selectedIndex];
      var url = opt && opt.getAttribute('data-image');
      var img = pick.querySelector('[data-tc-pick-fig] img');
      if (!img || !url) return;
      if (!img.__tcShot) img.__tcShot = { src: img.src, srcset: img.srcset || '' };
      img.srcset = '';
      img.sizes = '';
      img.src = url;
    }

    var sels = form.querySelectorAll('[data-tc-piece]');
    for (var j = 0; j < sels.length; j++) {
      (function (sel) {
        sel.addEventListener('change', function () {
          recalc();
          var pick = sel.closest('.tc-pick');
          if (pick) reshoot(pick, sel);
        });
      }(sels[j]));
    }
  }


  /* ---- The banner -----------------------------------------------------
     Slides are already stacked and the first is already marked active by the
     template, so this only moves between them. Everything that can interrupt
     an automatic rotation does: hover, focus, a swipe, the tab going to the
     background, and a stated preference for less motion. A banner that keeps
     moving under someone's cursor while they aim at its button is the reason
     people distrust carousels. */
  function hero(root) {
    if (root.__tcHero) return;
    root.__tcHero = true;

    var slides = root.querySelectorAll('[data-tc-slide]');
    if (slides.length < 2) return;

    /* The slides behind the first carry their photographs in data-srcset
       (see tc-hero.liquid) so the browser does not fetch them ahead of the
       one it is painting. They are filled in once the page has loaded, or
       sooner if somebody steers to them first. */
    function wake() {
      var late = root.querySelectorAll('[data-tc-defer]');
      for (var i = 0; i < late.length; i++) {
        var els = late[i].querySelectorAll('[data-srcset], [data-src]');
        for (var j = 0; j < els.length; j++) {
          var set = els[j].getAttribute('data-srcset');
          var src = els[j].getAttribute('data-src');
          if (set) { els[j].setAttribute('srcset', set); els[j].removeAttribute('data-srcset'); }
          if (src) { els[j].setAttribute('src', src); els[j].removeAttribute('data-src'); }
        }
        late[i].removeAttribute('data-tc-defer');
      }
    }
    if (document.readyState === 'complete') setTimeout(wake, 300);
    else window.addEventListener('load', function () { setTimeout(wake, 300); }, { once: true });
    var dots = root.querySelectorAll('[data-tc-dot]');
    var at = 0;
    var timer = null;
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var wait = (parseInt(root.getAttribute('data-interval'), 10) || 0) * 1000;

    function show(next) {
      wake();
      at = (next + slides.length) % slides.length;
      for (var i = 0; i < slides.length; i++) {
        if (i === at) slides[i].setAttribute('data-active', 'on');
        else slides[i].removeAttribute('data-active');
        if (dots[i]) dots[i].setAttribute('aria-selected', i === at ? 'true' : 'false');
      }
    }

    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      stop();
      if (!wait || still) return;
      timer = setInterval(function () { show(at + 1); }, wait);
    }
    /* A press means they are steering. Rotating again afterwards would fight
       them, so the timer stays off for the rest of the visit. */
    function steer(to) { wait = 0; stop(); show(to); }

    var prev = root.querySelector('[data-tc-prev]');
    var next = root.querySelector('[data-tc-next]');
    if (prev) prev.addEventListener('click', function () { steer(at - 1); });
    if (next) next.addEventListener('click', function () { steer(at + 1); });
    for (var d = 0; d < dots.length; d++) {
      (function (i) { dots[i].addEventListener('click', function () { steer(i); }); }(d));
    }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    /* Swipe. Only a decisive sideways drag counts, so a finger travelling down
       the page never flicks the banner along with it. */
    var x0 = null, y0 = null;
    root.addEventListener('touchstart', function (e) {
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) steer(dx < 0 ? at + 1 : at - 1);
    }, { passive: true });

    start();
  }

  function boot(scope) {
    var heroes = (scope || document).querySelectorAll('[data-tc-hero]');
    for (var h = 0; h < heroes.length; h++) hero(heroes[h]);
    /* The outfit tabs and the new-in tabs are the same mechanism. */
    var roots = (scope || document).querySelectorAll('[data-tc-look], [data-tc-tabset]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
    var forms = (scope || document).querySelectorAll('[data-tc-look-form]');
    for (var k = 0; k < forms.length; k++) totals(forms[k]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
